// src/components/EventDetailsPanel.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../config/supabase';
import { format, parseISO } from 'date-fns';

const { FiX, FiMusic, FiBookOpen, FiSave, FiLoader } = FiIcons;

/** Skeleton layout for the drawer while loading */
const DrawerSkeleton = ({ variant = 'overlay', inlineTransition = 'enter' }) => {
  const inlineShell = variant === 'inline';
  const swap = inlineShell && inlineTransition === 'swap';
  return (
    <motion.div
      initial={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
      className={`h-full w-full bg-white flex flex-col ${inlineShell ? '' : 'fixed right-0 top-0 w-96 shadow-2xl border-l border-gray-200 z-50'}`}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 bg-gray-200 rounded-full animate-pulse" />
          <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="space-y-3">
            <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
            <div className="h-10 w-full bg-gray-200 rounded animate-pulse" />
          </div>
        ))}
        <div className="space-y-3">
          <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
          <div className="h-24 w-full bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
      <div className="border-t border-gray-200 p-4">
        <div className="h-4 w-48 bg-gray-200 rounded animate-pulse mx-auto" />
      </div>
    </motion.div>
  );
};

const EventDetailsPanel = ({ event, onClose, variant = 'overlay', inlineTransition = 'enter' }) => {
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [editableFields, setEditableFields] = useState({
    piece: '',
    composer: '',
    notification_sent: false,
    notes: '',
    program: ''
  });

  useEffect(() => {
    if (event) {
      fetchEventDetails();
    }
  }, [event]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      if (event.type === 'performance') {
        const { data, error } = await supabase
          .from('performance_students')
          .select(`
            id,
            piece,
            composer,
            notification_sent,
            notes,
            performances (
              id,
              title,
              performance_date,
              performance_time,
              location,
              description
            )
          `)
          .eq('id', event.studentRecordId)
          .single();

        if (error) throw error;

        setEventData({
          ...data,
          type: 'performance',
          event_info: data.performances
        });

        setEditableFields({
          piece: data.piece || '',
          composer: data.composer || '',
          notification_sent: data.notification_sent || false,
          notes: data.notes || '',
          program: ''
        });

      } else if (event.type === 'exam') {
        const { data, error } = await supabase
          .from('exam_students')
          .select(`
            id,
            grade,
            type,
            program,
            entry_completed,
            scheduled_date,
            scheduled_time,
            location,
            exams (
              id,
              name,
              session,
              opening_date,
              closing_date,
              location,
              notes
            )
          `)
          .eq('id', event.studentRecordId)
          .single();

        if (error) throw error;

        setEventData({
          ...data,
          type: 'exam',
          event_info: data.exams
        });

        setEditableFields({
          piece: '',
          composer: '',
          notification_sent: false,
          notes: '',
          program: data.program || ''
        });
      }

    } catch (err) {
      console.error('Error fetching event details:', err);
      setError('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditableFields(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (eventData.type === 'performance') {
        const { error } = await supabase
          .from('performance_students')
          .update({
            piece: editableFields.piece,
            composer: editableFields.composer,
            notification_sent: editableFields.notification_sent,
            notes: editableFields.notes,
            updated_at: new Date().toISOString()
          })
          .eq('id', event.studentRecordId);
        if (error) throw error;

      } else if (eventData.type === 'exam') {
        const { error } = await supabase
          .from('exam_students')
          .update({
            program: editableFields.program,
            updated_at: new Date().toISOString()
          })
          .eq('id', event.studentRecordId);
        if (error) throw error;
      }

      setHasChanges(false);
    } catch (err) {
      console.error('Error saving event details:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const formatDateTime = (date, time) => {
    try {
      if (!date) return 'Not specified';
      const dateObj = parseISO(date);
      let formatted = format(dateObj, 'EEEE, MMMM d, yyyy');
      if (time) {
        const [hours, minutes] = time.split(':');
        const timeObj = new Date();
        timeObj.setHours(parseInt(hours), parseInt(minutes));
        formatted += ` at ${format(timeObj, 'h:mm a')}`;
      }
      return formatted;
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) return <DrawerSkeleton variant={variant} inlineTransition={inlineTransition} />;

  if (error || !eventData) {
    const inlineShell = variant === 'inline';
    const swap = inlineShell && inlineTransition === 'swap';
    return (
      <motion.div
       initial={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
        className={`h-full w-full bg-white ${inlineShell ? '' : 'fixed right-0 top-0 w-96 shadow-2xl border-l border-gray-200 z-50'}`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Error</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors">
            <SafeIcon icon={FiX} className="text-gray-600" />
          </button>
        </div>
        <div className="p-4 flex items-center justify-center h-32">
          <p className="text-red-600 text-center">{error || 'Failed to load event details'}</p>
        </div>
      </motion.div>
    );
  }

  const isPerformance = eventData.type === 'performance';
  const eventInfo = eventData.event_info;
  const inlineShell = variant === 'inline';
  const swap = inlineShell && inlineTransition === 'swap';

  return (
    <motion.div
     initial={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: inlineShell && !swap ? 50 : 300 }}
      className={`h-full w-full bg-white flex flex-col ${inlineShell ? '' : 'fixed right-0 top-0 w-96 shadow-2xl border-l border-gray-200 z-50'}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center">
          <SafeIcon
            icon={isPerformance ? FiMusic : FiBookOpen}
            className={`mr-2 ${isPerformance ? 'text-purple-500' : 'text-green-500'}`}
          />
          <h2 className="text-lg font-semibold text-gray-900">
            {isPerformance ? 'Performance' : 'Exam'} Details
          </h2>
        </div>
        <div className="flex items-center space-x-2">
          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              <SafeIcon icon={saving ? FiLoader : FiSave} className={`mr-1 ${saving ? 'animate-spin' : ''}`} />
              {saving ? 'Saving...' : 'Save'}
            </button>
          )}
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors">
            <SafeIcon icon={FiX} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Event Information */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Event Information</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Name</label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {isPerformance ? eventInfo.title : eventInfo.name}
                {!isPerformance && eventInfo.session && ` - ${eventInfo.session}`}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Date & Time</label>
              <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                {isPerformance
                  ? formatDateTime(eventInfo.performance_date, eventInfo.performance_time)
                  : formatDateTime(eventData.scheduled_date || eventInfo.opening_date, eventData.scheduled_time)
                }
              </p>
            </div>

            {(eventInfo.location || eventData.location) && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Location</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                  {eventData.location || eventInfo.location}
                </p>
              </div>
            )}

            {(eventInfo.description || eventInfo.notes) && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                  {eventInfo.description || eventInfo.notes}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Performance-specific fields */}
        {isPerformance && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Performance Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Piece</label>
                <input
                  type="text"
                  value={editableFields.piece}
                  onChange={(e) => handleFieldChange('piece', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Enter piece name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Composer</label>
                <input
                  type="text"
                  value={editableFields.composer}
                  onChange={(e) => handleFieldChange('composer', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Enter composer name"
                />
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editableFields.notification_sent}
                    onChange={(e) => handleFieldChange('notification_sent', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-600">Notification Sent</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Notes</label>
                <textarea
                  value={editableFields.notes}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Enter any additional notes"
                />
              </div>
            </div>
          </div>
        )}

        {/* Exam-specific fields */}
        {!isPerformance && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Exam Details</h3>
            <div className="space-y-4">
              {eventData.grade && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Grade</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{eventData.grade}</p>
                </div>
              )}

              {eventData.type && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Type</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{eventData.type}</p>
                </div>
              )}

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={eventData.entry_completed || false}
                    disabled
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 opacity-60"
                  />
                  <span className="text-sm font-medium text-gray-600">Entry Completed</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Program</label>
                <textarea
                  value={editableFields.program}
                  onChange={(e) => handleFieldChange('program', e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-h-[8rem] resize-y"
                  placeholder="List program details (one piece per line)…"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Supports multiple lines. Use one line per piece; add notes as needed.
                </p>
              </div>

              {eventInfo.opening_date && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Opening Date</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {format(parseISO(eventInfo.opening_date), 'EEEE, MMMM d, yyyy')}
                  </p>
                </div>
              )}

              {eventInfo.closing_date && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Closing Date</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                    {format(parseISO(eventInfo.closing_date), 'EEEE, MMMM d, yyyy')}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {hasChanges && (
        <div className="border-t border-gray-200 p-4 bg-blue-50">
          <p className="text-sm text-blue-700 text-center">You have unsaved changes</p>
        </div>
      )}
    </motion.div>
  );
};

export default EventDetailsPanel;